import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const AboutPage: React.FC = () => (
  <div className="min-h-screen bg-bg-page pt-24">
    {/* Hero */}
    <div className="bg-gradient-to-br from-[#0F172A] to-[#1A2540] py-16 text-white">
      <div className="w-full px-6 xl:px-16 text-center">
        <div className="text-brand-gold text-sm font-semibold uppercase tracking-wider mb-2">
          Kurumsal
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">Hakkımızda</h1>
        <p className="text-white/70 max-w-2xl mx-auto leading-relaxed">
          Bursa Trabzonlular Derneği, 2005 yılında Bursa'da yaşayan Trabzonluların bir araya gelmesiyle kuruldu. Kültürü yaşatmak, dayanışmayı güçlendirmek ve geleceğe birlikte yürümek için varız.
        </p>
      </div>
    </div>

    <div className="w-full px-6 xl:px-16 py-16 space-y-16">
      {/* Mission / Vision / Values */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Misyon */}
        <div className="card border-t-4 border-brand-bordo flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-bordo/10 flex items-center justify-center">
              <i className="fa-solid fa-bullseye text-brand-bordo text-lg" />
            </div>
            <h3 className="font-bold text-lg text-brand-bordo">Misyonumuz</h3>
          </div>
          <p className="text-text-muted text-sm leading-relaxed">
            Bursa'daki Trabzonluları aynı çatı altında buluşturmak; Trabzon kültürünü, hafızasını ve dayanışma ruhunu yaşatarak geleceğe taşımak.
          </p>
          <ul className="space-y-2 mt-1">
            {[
              "Hemşehri dayanışmasını güçlendirmek ve yeni gelenlere kapıları açık tutmak",
              "Trabzon kültürünü, müziğini, mutfağını ve geleneklerini Bursa'da yaşatmak",
              "Gençlere burs, mentorluk ve kariyer desteği sunarak geleceğe yatırım yapmak",
              "Üyeler arasında güçlü sosyal bağlar kurarak birlik ve beraberliği pekiştirmek",
            ].map((m) => (
              <li key={m} className="flex items-start gap-2 text-sm text-text-muted">
                <i className="fa-solid fa-check text-brand-bordo text-xs mt-1 flex-shrink-0" />
                {m}
              </li>
            ))}
          </ul>
        </div>

        {/* Vizyon */}
        <div className="card border-t-4 border-brand-blue flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center">
              <i className="fa-solid fa-eye text-brand-blue text-lg" />
            </div>
            <h3 className="font-bold text-lg text-brand-blue">Vizyonumuz</h3>
          </div>
          <p className="text-text-muted text-sm leading-relaxed">
            Bursa'nın en köklü ve kapsayıcı hemşehri derneği olmak; kültürel bağı güçlendiren, gençlere umut sunan ve toplumsal katkı üreten sürdürülebilir bir yapı olmak.
          </p>
          <ul className="space-y-2 mt-1">
            {[
              "Trabzon ile Bursa arasında kalıcı bir gönül köprüsü olmak",
              "Türkiye'de örnek gösterilen bir hemşehri derneği modeli oluşturmak",
              "Kültürel etkinliklerle şehirlerarası diyaloğu ve saygıyı artırmak",
              "Kurumsal şeffaflık ve demokratik yönetimle güven inşa etmek",
            ].map((m) => (
              <li key={m} className="flex items-start gap-2 text-sm text-text-muted">
                <i className="fa-solid fa-check text-brand-blue text-xs mt-1 flex-shrink-0" />
                {m}
              </li>
            ))}
          </ul>
        </div>

        {/* Değerler */}
        <div className="card border-t-4 border-brand-gold flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-gold/10 flex items-center justify-center">
              <i className="fa-solid fa-star text-brand-gold text-lg" />
            </div>
            <h3 className="font-bold text-lg text-brand-gold">Değerlerimiz</h3>
          </div>
          <p className="text-text-muted text-sm leading-relaxed">
            Geçmişe bağlılık, geleceğe umut, her bireye saygı ve herkesi kapsayan bir aidiyet anlayışı. Köklü ama genç, vakarlı ama sıcak.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {[
              { icon: "fa-heart", label: "Dayanışma" },
              { icon: "fa-shield-halved", label: "Saygı" },
              { icon: "fa-users", label: "Kapsayıcılık" },
              { icon: "fa-seedling", label: "Geçmişe Bağlılık" },
              { icon: "fa-handshake", label: "Güven" },
              { icon: "fa-lightbulb", label: "Yenilikçilik" },
            ].map((v) => (
              <div key={v.label} className="flex items-center gap-2 text-sm text-text-muted">
                <i className={`fa-solid ${v.icon} text-brand-gold text-xs`} />
                {v.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Story */}
      <div className="card max-w-3xl mx-auto">
        <div className="text-brand-gold text-sm font-semibold uppercase tracking-wider mb-3">
          Derneğimizin Hikâyesi
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-5">
          Trabzon'dan Bursa'ya Uzanan Köprü
        </h2>
        <div className="space-y-4 text-text-muted leading-relaxed text-sm">
          <p>
            2005 yılında, Bursa'ya yerleşmiş bir grup Trabzonlunun girişimiyle hayata geçen derneğimiz, bugün 500'ü aşkın üyesiyle şehrin en köklü hemşehri kuruluşlarından biri haline gelmiştir.
          </p>
          <p>
            Kuruluşumuzun temelinde yalnızca nostalji değil; memleket bağını yaşatmak, yeni gelenlere el uzatmak, gençlere rehberlik etmek ve Bursa'ya Trabzon kültürünün değerlerini armağan etmek yatar.
          </p>
          <p>
            Yıllar içinde onlarca kültür etkinliği, yüzlerce aile buluşması, genç kuşaklara sunulan burs ve kariyer destekleriyle büyüyen derneğimiz; Trabzon ile Bursa arasında gönül köprüsü olmaya devam etmektedir.
          </p>
        </div>
        <div className="mt-6">
          <Link
            to="/tarihce/hikaye"
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            Tarihçeyi Oku <ArrowRight size={14} />
          </Link>
        </div>
      </div>

    </div>
  </div>
);

export default AboutPage;
